import { BinaryWriter } from "../binary.js";
import { ServerOp } from "../opcodes.js";
import { isDirection, isPvpMark, Direction, EntityKind, PvpMark } from "../constants.js";
/**
 * Bits do que mudou. Os campos presentes vêm **nesta ordem** no corpo do registro.
 *
 * O agrupamento não é arbitrário — cada bit junta os campos que **mudam juntos**. Posição
 * é `x`, `y` e `z` porque um passo muda os três (ou nenhum); aparência é `kind`, `vocation`
 * e `outfitTier` porque só uma troca de equipamento os move, e ela é rara. Separar em sete
 * bits independentes gastaria a mesma máscara para descrever combinações que não acontecem.
 */
export const SnapshotField = {
    Position: 1 << 0,
    Direction: 1 << 1,
    Step: 1 << 2,
    Health: 1 << 3,
    MaxHealth: 1 << 4,
    Look: 1 << 5,
    Pvp: 1 << 6,
};
/** Máscara de quem acabou de entrar em cena: tudo, porque o cliente não sabe nada dela. */
export const SNAPSHOT_ALL = SnapshotField.Position |
    SnapshotField.Direction |
    SnapshotField.Step |
    SnapshotField.Health |
    SnapshotField.MaxHealth |
    SnapshotField.Look |
    SnapshotField.Pvp;
/** Bytes do cabeçalho: op(1) + tick(4) + ackSeq(4) + count(2). */
export const SNAPSHOT_HEADER_SIZE = 11;
/** Tamanho de um registro com **todos** os campos: id(4) + mask(1) + 15 de corpo. */
export const ENTITY_RECORD_SIZE = 20;
export function encodeSnapshot(packet, writer = new BinaryWriter(SNAPSHOT_HEADER_SIZE + packet.entities.length * ENTITY_RECORD_SIZE)) {
    writer
        .u8(ServerOp.Snapshot)
        .u32(packet.tick)
        .u32(packet.ackSeq)
        .u16(packet.entities.length);
    for (const entity of packet.entities) {
        writer.u32(entity.id).u8(entity.mask);
        if (entity.mask & SnapshotField.Position) {
            writer.u16(entity.x ?? 0).u16(entity.y ?? 0).u8(entity.z ?? 0);
        }
        if (entity.mask & SnapshotField.Direction)
            writer.u8(entity.direction ?? Direction.South);
        if (entity.mask & SnapshotField.Step)
            writer.u16(entity.stepMs ?? 0);
        if (entity.mask & SnapshotField.Health)
            writer.u16(entity.health ?? 0);
        if (entity.mask & SnapshotField.MaxHealth)
            writer.u16(entity.maxHealth ?? 0);
        if (entity.mask & SnapshotField.Look) {
            writer.u8(entity.kind ?? EntityKind.Npc).u8(entity.vocation ?? 0).u8(entity.outfitTier ?? 0);
        }
        if (entity.mask & SnapshotField.Pvp)
            writer.u8(entity.pvpMark ?? PvpMark.None);
    }
    return writer;
}
export function decodeSnapshot(reader) {
    const tick = reader.u32();
    const ackSeq = reader.u32();
    const count = reader.u16();
    const entities = new Array(count);
    for (let i = 0; i < count; i++) {
        const id = reader.u32();
        const mask = reader.u8();
        const delta = { id, mask };
        if (mask & SnapshotField.Position) {
            delta.x = reader.u16();
            delta.y = reader.u16();
            delta.z = reader.u8();
        }
        if (mask & SnapshotField.Direction) {
            const raw = reader.u8();
            delta.direction = isDirection(raw) ? raw : Direction.South;
        }
        if (mask & SnapshotField.Step)
            delta.stepMs = reader.u16();
        if (mask & SnapshotField.Health)
            delta.health = reader.u16();
        if (mask & SnapshotField.MaxHealth)
            delta.maxHealth = reader.u16();
        if (mask & SnapshotField.Look) {
            // Um valor fora da lista vira jogador, e não um erro: um cliente antigo contra um
            // servidor com uma família de entidade nova precisa desenhar **alguma coisa**, e o
            // atlas de jogador é o único que sempre existe. É a mesma tolerância que o mapa tem
            // para espécie desconhecida.
            const kind = reader.u8();
            delta.kind =
                kind === EntityKind.Npc || kind === EntityKind.Door
                    ? kind
                    : EntityKind.Player;
            delta.vocation = reader.u8();
            delta.outfitTier = reader.u8();
        }
        if (mask & SnapshotField.Pvp) {
            const raw = reader.u8();
            delta.pvpMark = isPvpMark(raw) ? raw : PvpMark.None;
        }
        entities[i] = delta;
    }
    return { tick, ackSeq, entities };
}
/**
 * Aplica o delta sobre o que o observador já sabia, devolvendo o estado completo das
 * entidades **mencionadas neste pacote**.
 *
 * Quem não aparece no pacote não mudou, e continua no `store` como estava — não é o mesmo
 * que ter saído de cena. A saída tem pacote próprio (`Despawn`), e é por isso que o delta
 * pode se dar ao luxo de omitir quem está parado.
 *
 * `store` é mutado no lugar, de propósito: ele é o estado do cliente, e criar um objeto
 * novo por entidade por tick seria alocação no caminho quente — exatamente o que o
 * PRD §3.1 proíbe do outro lado do fio e que vale igual aqui, onde o coletor de lixo
 * concorre com o quadro.
 */
export function applySnapshot(store, packet) {
    const seen = [];
    for (const delta of packet.entities) {
        let state = store.get(delta.id);
        if (!state) {
            // Entidade nova: o servidor manda todos os campos (`SNAPSHOT_ALL`), então os
            // padrões abaixo nunca são o que fica — eles existem para que um pacote truncado
            // por um servidor com defeito produza uma entidade inerte em vez de `undefined`
            // espalhado pelo renderizador.
            state = {
                id: delta.id,
                x: 0,
                y: 0,
                z: 0,
                direction: Direction.South,
                stepMs: 0,
                health: 0,
                maxHealth: 1,
                kind: EntityKind.Npc,
                vocation: 0,
                outfitTier: 0,
                pvpMark: PvpMark.None,
            };
            store.set(delta.id, state);
        }
        if (delta.mask & SnapshotField.Position) {
            state.x = delta.x ?? state.x;
            state.y = delta.y ?? state.y;
            state.z = delta.z ?? state.z;
        }
        if (delta.mask & SnapshotField.Direction)
            state.direction = delta.direction ?? state.direction;
        if (delta.mask & SnapshotField.Health)
            state.health = delta.health ?? state.health;
        if (delta.mask & SnapshotField.MaxHealth)
            state.maxHealth = delta.maxHealth ?? state.maxHealth;
        if (delta.mask & SnapshotField.Look) {
            state.kind = delta.kind ?? state.kind;
            state.vocation = delta.vocation ?? state.vocation;
            state.outfitTier = delta.outfitTier ?? state.outfitTier;
        }
        if (delta.mask & SnapshotField.Pvp)
            state.pvpMark = delta.pvpMark ?? state.pvpMark;
        // `stepMs` descreve um **evento**, não um estado: ele vale no tick em que o passo
        // aconteceu e zera nos seguintes. Por isso é o único campo que precisa ser limpo
        // quando não vem no delta — sem isso, uma entidade que andou uma vez continuaria
        // sendo animada em todo quadro seguinte.
        state.stepMs = delta.mask & SnapshotField.Step ? (delta.stepMs ?? 0) : 0;
        seen.push(state);
    }
    return seen;
}
/**
 * O que mudou entre dois estados. `SNAPSHOT_ALL` quando não havia estado anterior.
 *
 * Mora no protocolo, e não no servidor, porque é a contraparte exata de `applySnapshot`:
 * as duas precisam concordar sobre quais campos cada bit cobre, e é o tipo de par que
 * diverge no primeiro campo novo se estiver escrito em dois pacotes diferentes.
 */
export function diffEntity(previous, current) {
    if (!previous)
        return SNAPSHOT_ALL;
    let mask = 0;
    if (previous.x !== current.x || previous.y !== current.y || previous.z !== current.z) {
        mask |= SnapshotField.Position;
    }
    if (previous.direction !== current.direction)
        mask |= SnapshotField.Direction;
    // `stepMs` entra sempre que for diferente de zero **ou** que tenha acabado de zerar: o
    // primeiro caso é o passo acontecendo, e o segundo... não existe. Zerar é o padrão de
    // `applySnapshot` quando o bit não vem, e por isso só o valor não nulo precisa trafegar.
    if (current.stepMs !== 0)
        mask |= SnapshotField.Step;
    if (previous.health !== current.health)
        mask |= SnapshotField.Health;
    if (previous.maxHealth !== current.maxHealth)
        mask |= SnapshotField.MaxHealth;
    if (previous.kind !== current.kind ||
        previous.vocation !== current.vocation ||
        previous.outfitTier !== current.outfitTier) {
        mask |= SnapshotField.Look;
    }
    if (previous.pvpMark !== current.pvpMark)
        mask |= SnapshotField.Pvp;
    return mask;
}
/** Monta o registro a enviar a partir do estado atual e da máscara. */
export function toDelta(state, mask) {
    const delta = { id: state.id, mask };
    if (mask & SnapshotField.Position) {
        delta.x = state.x;
        delta.y = state.y;
        delta.z = state.z;
    }
    if (mask & SnapshotField.Direction)
        delta.direction = state.direction;
    if (mask & SnapshotField.Step)
        delta.stepMs = state.stepMs;
    if (mask & SnapshotField.Health)
        delta.health = state.health;
    if (mask & SnapshotField.MaxHealth)
        delta.maxHealth = state.maxHealth;
    if (mask & SnapshotField.Look) {
        delta.kind = state.kind;
        delta.vocation = state.vocation;
        delta.outfitTier = state.outfitTier;
    }
    if (mask & SnapshotField.Pvp)
        delta.pvpMark = state.pvpMark;
    return delta;
}
//# sourceMappingURL=snapshot.js.map